/**
 * Typed HTTP client.
 *
 * Every response is validated against the shared contracts, so a back-end
 * change that the front-end has not caught up with fails loudly here instead
 * of rendering as `undefined` somewhere deep in a table.
 */
import {
  addSlotsResult,
  adminDaySlots,
  adminSiteSummary,
  apiErrorSchema,
  currentUser,
  dashboardView,
  deliveryList,
  intervenantView,
  isoDay,
  reservationView,
  siteDetail,
  siteSummary,
  stockRow,
  userView,
  type AddSlotsBody,
  type AdjustStockBody,
  type ApiError,
  type CreateReservationBody,
  type ErrorCode,
  type LoginBody,
  type PatchStockBody,
  type SetFulfillmentBody,
  type UpsertIntervenantBody,
  type UpsertUserBody,
} from "@soi/contracts";
import { z } from "zod";

/** An error the interface can act on, carrying the server's stable code. */
export class ApiFailure extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(error: ApiError, status: number) {
    super(error.message);
    this.name = "ApiFailure";
    this.code = error.code;
    this.status = status;
    this.details = error.details;
  }
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    // Sessions ride on a cookie; without this the back office never logs in.
    credentials: "same-origin",
    headers:
      init.body === undefined ? init.headers : { "Content-Type": "application/json", ...init.headers },
    ...init,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const parsed = apiErrorSchema.safeParse(body);
    throw new ApiFailure(
      parsed.success
        ? parsed.data
        : { code: "ERREUR_INTERNE", message: "Le service est momentanément indisponible." },
      res.status,
    );
  }

  if (res.status === 204) return schema.parse(undefined);
  return schema.parse(await res.json());
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

export const api = {
  /* --- session -------------------------------------------------------- */
  login: (body: LoginBody) =>
    request("/api/auth/login", currentUser, { method: "POST", ...json(body) }),
  logout: () =>
    request("/api/auth/logout", z.object({ ok: z.boolean() }), { method: "POST" }),
  me: () => request("/api/auth/me", currentUser),

  /* --- public catalogue ------------------------------------------------ */
  sites: () => request("/api/catalog/sites", z.array(siteSummary)),
  site: (slug: string) => request(`/api/catalog/sites/${slug}`, siteDetail),
  zones: () => request("/api/catalog/zones", z.array(z.string())),

  /* --- booking --------------------------------------------------------- */
  book: (body: CreateReservationBody) =>
    request("/api/reservations", reservationView, { method: "POST", ...json(body) }),
  reservation: (token: string) => request(`/api/reservations/${token}`, reservationView),
  cancelReservation: (token: string) =>
    request(`/api/reservations/${token}`, reservationView, { method: "DELETE" }),
  // Inside a building, cancelled bookings stay listed: an operator who has
  // just cancelled one needs to see that it happened, and a slot freed last
  // week is worth seeing next to the round it belonged to.
  adminReservations: (siteSlug?: string) =>
    request(
      siteSlug
        ? `/api/admin/reservations?status=all&siteSlug=${encodeURIComponent(siteSlug)}`
        : "/api/admin/reservations",
      z.array(reservationView),
    ),
  adminReservationSites: () =>
    request("/api/admin/reservations/sites", z.array(adminSiteSummary)),
  adminCancelReservation: (id: string) =>
    request(`/api/admin/reservations/${id}`, reservationView, { method: "DELETE" }),
  adminSendReminder: (id: string) =>
    request(`/api/admin/reservations/${id}/reminder`, z.object({ queued: z.boolean() }), {
      method: "POST",
    }),
  adminSetFulfillment: (id: string, body: SetFulfillmentBody) =>
    request(`/api/admin/reservations/${id}/fulfillment`, reservationView, {
      method: "PATCH",
      ...json(body),
    }),

  /* --- intervenant workspace ------------------------------------------- */
  myDeliveries: () => request("/api/me/deliveries", deliveryList),
  validateDelivery: (slotId: string) =>
    request(
      `/api/me/deliveries/${slotId}`,
      z.object({ slotId: z.string(), delivered: z.boolean() }),
      { method: "POST" },
    ),
  cancelDelivery: (slotId: string) =>
    request(
      `/api/me/deliveries/${slotId}`,
      z.object({ slotId: z.string(), delivered: z.boolean() }),
      { method: "DELETE" },
    ),

  /* --- stock ----------------------------------------------------------- */
  stock: () => request("/api/stock", z.array(stockRow)),
  patchStock: (slug: string, body: PatchStockBody) =>
    request(`/api/stock/${slug}`, stockRow, { method: "PATCH", ...json(body) }),
  adjustStock: (slug: string, body: AdjustStockBody) =>
    request(`/api/stock/${slug}/adjust`, stockRow, { method: "POST", ...json(body) }),

  /* --- operator -------------------------------------------------------- */
  dashboard: () => request("/api/dashboard", dashboardView),
  intervenants: () => request("/api/intervenants", z.array(intervenantView)),
  createIntervenant: (body: UpsertIntervenantBody) =>
    request("/api/intervenants", intervenantView, { method: "POST", ...json(body) }),
  updateIntervenant: (code: string, body: Partial<UpsertIntervenantBody>) =>
    request(`/api/intervenants/${code}`, intervenantView, { method: "PATCH", ...json(body) }),
  deactivateIntervenant: (code: string) =>
    request(
      `/api/intervenants/${code}`,
      z.object({ code: z.string(), isActive: z.boolean() }),
      { method: "DELETE" },
    ),

  /* --- operator accounts ------------------------------------------------ */
  users: () => request("/api/users", z.array(userView)),
  createUser: (body: UpsertUserBody) =>
    request("/api/users", userView, { method: "POST", ...json(body) }),
  updateUser: (id: string, body: Partial<UpsertUserBody>) =>
    request(`/api/users/${id}`, userView, { method: "PATCH", ...json(body) }),
  deactivateUser: (id: string) =>
    request(`/api/users/${id}`, z.object({ id: z.string(), isActive: z.boolean() }), {
      method: "DELETE",
    }),

  /* --- admin: extra slots ------------------------------------------------ */
  adminSlotDays: () => request("/api/admin/slots/days", z.object({ days: z.array(isoDay) })),
  adminDaySlots: (day: string) =>
    request(`/api/admin/slots/day?day=${encodeURIComponent(day)}`, adminDaySlots),
  addSlots: (body: AddSlotsBody) =>
    request("/api/admin/slots", addSlotsResult, { method: "POST", ...json(body) }),
};
